import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Checkbox, CheckboxStyles, Radio, Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { getBoundArrivalCity, getBoundDepartureCity } from '@/app/components/hotel-detail/flight-train/TransportCommon';
import { useTransportIsChangingFilters } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';

export const EvoFilterSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const EvoFilterDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  /* MPH-398: Figma leaves 16px (space200) above and below section dividers */
  margin: 16px 0;
`;

const SectionHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* MPH-474: Figma section titles have no padding above and 8px before the option list */
  padding: 0 0 8px;
`;

export function EvoFilterSectionHeader(props: { title: string; subtitle?: string }) {
  const { title, subtitle } = props;
  return (
    <SectionHeaderContainer>
      <Text variant="Title4">{title}</Text>
      {subtitle ? (
        <Text variant="Body" color="textPrimary">
          {subtitle}
        </Text>
      ) : null}
    </SectionHeaderContainer>
  );
}

export function EvoBoundHeader(props: { type: BoundType }) {
  const { type } = props;
  const { t } = useTranslation();
  const { data: selectedTransportData } = useSelectedTransport();
  const outboundData = selectedTransportData?.transports[0]?.outbound;
  const departureCityName = outboundData ? getBoundDepartureCity(outboundData) : undefined;
  const arrivalCityName = outboundData ? getBoundArrivalCity(outboundData) : undefined;
  const subtitle =
    departureCityName && arrivalCityName
      ? type === 'outbound'
        ? `${departureCityName} - ${arrivalCityName}`
        : `${arrivalCityName} - ${departureCityName}`
      : undefined;
  return (
    <EvoFilterSectionHeader
      title={
        type === 'outbound'
          ? t('vacation.oss.change_transport.filters.travel_times.outbound')
          : t('vacation.oss.change_transport.filters.travel_times.inbound')
      }
      subtitle={subtitle}
    />
  );
}

const CheckboxRowContainer = styled.div`
  width: 100%;

  ${CheckboxStyles.LabelText} {
    flex: 1;
    display: flex;
    align-items: center;
  }

  /* MPH-474: Figma gap between the 18px control and the label is 12px; the DS Checkbox renders 8px */
  span[class*='LabelContent'] {
    margin-left: ${({ theme }) => theme.kallisto.space.space150};
  }
`;

/* MPH-474: DS Radio defaults don't match the Figma filter option rows: 14px label (Figma Body 16px),
   8px control-label gap (Figma 12px) and ~50px row (Figma 48px). */
export const EvoFilterRadio = styled(Radio)`
  min-height: 48px;
  padding: 0;
  overflow: visible;

  span[class*='RadioLabel'] {
    ${({ theme }) => theme.kallisto.mixins.font('Body')};
  }

  span[class*='LeftRadioLabel'] {
    margin-left: ${({ theme }) => theme.kallisto.space.space150};
  }
`;

const RowRightLabel = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  margin-left: auto;
  padding-left: 8px;
  white-space: nowrap;
`;

type EvoCheckboxRowProps = {
  id: string;
  label: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  rightLabel?: ReactNode;
  subLabel?: ReactNode;
};

export function EvoCheckboxRow(props: EvoCheckboxRowProps) {
  const { id, label, checked, disabled, onChange, rightLabel, subLabel } = props;
  return (
    <CheckboxRowContainer>
      <Checkbox
        checkbox={{
          id,
          label: rightLabel ? (
            <>
              <span>{label}</span>
              <RowRightLabel>{rightLabel}</RowRightLabel>
            </>
          ) : (
            label
          ),
        }}
        isChecked={checked}
        disabled={disabled}
        onChange={onChange}
        fullWidth={true}
        padding={{ ver: 1.5, hor: 0 }}
        secondaryLabel={subLabel}
        secondaryLabelPosition="SECOND_LINE"
      />
    </CheckboxRowContainer>
  );
}

const LoadingLayer = styled.div<{ $isCovering: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -8px;
  right: -8px;
  background: ${({ theme }) => theme.kallisto.color.common.white[1000]};
  opacity: ${({ $isCovering }) => ($isCovering ? 0.7 : 0)};
  visibility: ${({ $isCovering }) => ($isCovering ? 'visible' : 'hidden')};
  transition: 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  z-index: 1;
`;

export function EvoFilterLoadingLayer(props: { origins: TransportFiltersOrigin[] }) {
  const { origins } = props;
  const { isChanging, changingFilter } = useTransportIsChangingFilters();
  const isCovering = isChanging && (changingFilter === undefined || !origins.includes(changingFilter));
  return <LoadingLayer $isCovering={isCovering} aria-hidden="true" />;
}
